"use client";

import styles from "./Input.module.scss";
import classNames from "classnames";
import { InputTypes } from "./Input.types";
import { EyeIcon, EyeOffIcon } from "@vetty/icons";
import { useState } from "react";

const Input = ({
  onChange,
  label,
  placeholder,
  type = "text",
  defaultValue = "",
  required = false,
  variant = "default",
  labelSize = "small",
  id = "",
  className = "",
  fieldClassname = "",
  textSize = "small",
  suffix = "",
  prefix = "",
  maxLength,
  size = "large",
  disabled = false,
  onKeyDown = () => {},
  subLabel = "",
  disableShowPassword = false,
  autoFocus = false,
}: InputTypes) => {
  const [inputType, setInputType] = useState(type);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    if (
      type === "number" &&
      !/^[0-9,]$/.test(e.key) &&
      !["Backspace", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
    ) {
      e.preventDefault();
    }

    if (
      type === "number" &&
      maxLength &&
      e.currentTarget.value.length >= maxLength &&
      /^[0-9,]$/.test(e.key)
    ) {
      e.preventDefault();
    }

    onKeyDown(e);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (type === "number") {
      const pastedData = e.clipboardData.getData("text");
      if (!/^[0-9,]*$/.test(pastedData)) {
        e.preventDefault();
      } else if (
        maxLength &&
        e.currentTarget.value.length + pastedData.length > maxLength
      ) {
        e.preventDefault();
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    onChange(value);
  };

  const handleShowPassword = () => {
    if (disableShowPassword) return;
    setInputType(inputType === "text" ? "password" : "text");
  };

  return (
    <div className={classNames(styles.input, className)}>
      {label && (
        <label
          className={classNames(
            styles.input__label,
            styles[`input__label--${variant}`],
            styles[`input__label--${labelSize}`]
          )}
        >
          <span>
            {label} {required && "*"}
          </span>
        </label>
      )}

      {subLabel && <div className={styles["input__sub-label"]}>{subLabel}</div>}

      <div
        className={classNames(
          styles["input__field"],
          styles[`input__field--size-${size}`],
          styles[`input__field--${variant}`],
          styles[`input__field--${textSize}`],
          disabled && styles["input__field--disabled"],
          fieldClassname
        )}
      >
        {prefix && <span>{prefix}</span>}
        <input
          autoFocus={autoFocus}
          id={id}
          disabled={disabled}
          defaultValue={defaultValue}
          onChange={handleChange}
          type={inputType}
          placeholder={placeholder}
          maxLength={maxLength}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onWheel={(e: any) => e.target.blur()}
        />

        {type === "password" && !disableShowPassword && (
          <div
            onClick={handleShowPassword}
            className={styles["input__field__eye"]}
          >
            {type === "password" && inputType === "text" ? (
              <EyeOffIcon />
            ) : (
              <EyeIcon />
            )}
          </div>
        )}

        {suffix && <span dangerouslySetInnerHTML={{ __html: suffix }} />}
      </div>
    </div>
  );
};

export { Input };
